// hooks/useChartData.ts
import { useQueries } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useInView } from 'react-intersection-observer'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { postRequest } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'

type ApiEndpoint = string

interface ChartDependency {
  endpoints: ApiEndpoint[]
  isReady?: boolean
}

interface ChartConfig {
  id: string
  isNoComparison?: boolean
  dependencies: ChartDependency
}

// Example chart configurations
export const CHART_CONFIGS: Record<string, ChartConfig> = {
  conversionFunnel: {
    id: 'conversion_funnel',
    dependencies: {
      endpoints: ['/conversion-funnel']
    }
  }
}

interface UseChartDataProps {
  chartConfig: ChartConfig
  refreshKey?: string
  /** When set, skips AnalyticsContext date/channel filters (e.g. home metrics always use Today). */
  filtersOverride?: AnalyticsFilters
}

export type AnalyticsFilters = {
  channel?: string[]
  timeRange: {
    start: string
    end: string
  }
  timeScale: 'daily' | 'hourly'
  comparisonTimeRange?: {
    start: string
    end: string
  }
}

const fetchData = (endPoint: string, currentFilters: AnalyticsFilters) => {
  const request = postRequest(`/dashboard/widget${endPoint}`, apiURI.CHECKOUT_NODE_V2, currentFilters)
  return request
}

export function useChartData({ chartConfig, refreshKey, filtersOverride }: UseChartDataProps) {
  const {
    getters: { filters: contextFilters }
  } = useAnalytics()
  const filters = filtersOverride ?? contextFilters
  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: false
  })

  const combine = useCallback((results: any) => {
    return {
      data: results.map((result: any) => result.data),
      error: results.map((result: any) => result.error),
      isLoading: results.some((result: any) => result.isPending),
      isFetching: results.some((result: any) => result.isFetching),
      isError: results.some((result: any) => result.isError)
    }
  }, [])

  // Create queries for all dependencies
  const combinedQueries = useQueries({
    queries: chartConfig.dependencies.endpoints.map(endpoint => ({
      queryKey: [
        refreshKey,
        endpoint,
        filters,
        filters.comparisonTimeRange?.start,
        filters.comparisonTimeRange?.end,
        filters.timeRange.start,
        filters.timeRange.end
      ],
      queryFn: () => fetchData(endpoint, filters),
      enabled: inView, // Only fetch when in view
      staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
      cacheTime: 10 * 60 * 1000 // Keep in cache for 10 minutes
    })),
    combine: combine
  })

  return {
    ref, // Attach this to your chart container
    ...combinedQueries
  }
}
